import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExploreLayout from '../components/ExploreLayout';
import useExplore from '../hooks/useExplore';

const industries = [
  { value: 'restaurant', icon: '🍽️', label: 'Restaurant / Cafe', description: 'Opening duties, stock, shifts, standards and delivery flow.' },
  { value: 'retail', icon: '🏪', label: 'Retail', description: 'Multi-site standards, inventory trust and team accountability.' },
  { value: 'hospitality', icon: '🏨', label: 'Hospitality', description: 'Departments, housekeeping, handovers and daily service standards.' },
  { value: 'warehouse', icon: '🏭', label: 'Warehouse', description: 'Teams, handovers, resources, movement and coordination.' },
  { value: 'security', icon: '🛡️', label: 'Security', description: 'Patrols, incidents, proof, escalations and command visibility.' },
  { value: 'other', icon: '🧩', label: 'Other', description: 'Create a general operational journey.' },
];

const challenges = [
  { value: 'execution', icon: '✅', label: 'Staff forget work', route: '/explore/neo', description: 'Duties are missed unless someone keeps chasing.' },
  { value: 'late-visibility', icon: '⏰', label: 'Managers find problems late', route: '/explore/neo', description: 'Issues only become visible after the damage is done.' },
  { value: 'stock', icon: '📦', label: 'Stock cannot be trusted', route: '/explore/morpheus', description: 'Counts happen, but the truth still feels unclear.' },
  { value: 'dispatch', icon: '🚚', label: 'Deliveries become chaos', route: '/explore/link', description: 'Stores, drivers and customer flow do not stay connected.' },
  { value: 'everything', icon: '🌐', label: 'Show me everything', route: '/explore/neo', description: 'Experience the full MatrixOS operating system.' },
];

const buildSteps = [
  'Creating organization',
  'Adding stores',
  'Hiring demo staff',
  'Building standards',
  'Preparing inventory',
  'Starting today\'s operation',
];

function getRecommendedRoute(challenge) {
  return challenges.find((item) => item.value === challenge)?.route || '/explore/neo';
}

export default function ExploreIntroPage() {
  const navigate = useNavigate();
  const { state, setAnswer, completeStep } = useExplore();
  const answers = state.answers || {};
  const [stage, setStage] = useState(() => (answers.companyName ? 1 : 0));
  const [companyName, setCompanyName] = useState(answers.companyName || '');
  const [industry, setIndustry] = useState(answers.industry || '');
  const [challenge, setChallenge] = useState(answers.primaryChallenge || '');
  const [isBuilding, setIsBuilding] = useState(false);
  const [visibleBuildSteps, setVisibleBuildSteps] = useState(0);

  const cleanCompanyName = companyName.trim();
  const selectedIndustry = useMemo(() => industries.find((item) => item.value === industry), [industry]);
  const selectedChallenge = useMemo(() => challenges.find((item) => item.value === challenge), [challenge]);

  const saveCompanyName = () => {
    if (!cleanCompanyName) return;
    setAnswer('companyName', cleanCompanyName);
    setStage(1);
  };

  const saveIndustry = (value) => {
    setIndustry(value);
    setAnswer('industry', value);
    setTimeout(() => setStage(2), 160);
  };

  const startBuild = (value) => {
    setChallenge(value);
    setAnswer('primaryChallenge', value);
    setAnswer('problems', [value]);
    setIsBuilding(true);
    setVisibleBuildSteps(0);

    buildSteps.forEach((_, index) => {
      setTimeout(() => setVisibleBuildSteps(index + 1), 420 * (index + 1));
    });

    setTimeout(() => {
      completeStep('start');
      navigate(getRecommendedRoute(value));
    }, 420 * (buildSteps.length + 1));
  };

  return (
    <ExploreLayout
      stepId="start"
      eyebrow="MatrixOS Explore"
      title="Don't watch a demo. Build your operation."
      subtitle="MatrixOS creates a guided operating world from your answers, then lets you run through the problems your business actually cares about."
      hideFooter
    >
      <div className="explore-wizard">
        <div className="explore-wizard-copy">
          <p className="explore-kicker">Public product experience</p>
          <h2>
            {stage === 0 && 'Before we show you MatrixOS, let us build your company.'}
            {stage === 1 && `Nice to meet you, ${cleanCompanyName || 'Operator'}.`}
            {stage === 2 && `What should ${cleanCompanyName || 'your company'} solve first?`}
          </h2>
          <p>
            {stage === 0 && 'No signup. No sales call. Start by naming the operation you want to imagine inside MatrixOS.'}
            {stage === 1 && 'Choose the world that feels closest to your daily operation. MatrixOS will shape the experience around it.'}
            {stage === 2 && 'Pick the operational pain that feels most familiar. MatrixOS will launch the best first mission automatically.'}
          </p>
        </div>

        <div className="explore-wizard-panel">
          {stage === 0 && (
            <div className="explore-company-step">
              <label htmlFor="matrixosExploreCompany">What is your company called?</label>
              <input
                id="matrixosExploreCompany"
                className="explore-big-input"
                type="text"
                placeholder="e.g. The Venue"
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') saveCompanyName();
                }}
                autoFocus
              />
              <button type="button" className="explore-primary-btn explore-wide-btn" onClick={saveCompanyName} disabled={!cleanCompanyName}>
                Build {cleanCompanyName || 'my operation'} →
              </button>
            </div>
          )}

          {stage === 1 && (
            <div className="explore-selection-grid compact">
              {industries.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`explore-select-tile ${industry === item.value ? 'selected' : ''}`}
                  onClick={() => saveIndustry(item.value)}
                >
                  <span>{item.icon}</span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </button>
              ))}
            </div>
          )}

          {stage === 2 && !isBuilding && (
            <div className="explore-selection-grid challenges">
              {challenges.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`explore-select-tile challenge ${challenge === item.value ? 'selected' : ''}`}
                  onClick={() => startBuild(item.value)}
                >
                  <span>{item.icon}</span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </button>
              ))}
            </div>
          )}

          {isBuilding && (
            <div className="explore-build-sequence">
              <div className="explore-build-orb">M</div>
              <p className="explore-kicker">Building {cleanCompanyName || 'your operation'}</p>
              <h3>{selectedChallenge?.label || 'Preparing your first mission'}</h3>
              {selectedIndustry && <p className="explore-muted-line">Industry world: {selectedIndustry.label}</p>}
              <div className="explore-build-lines">
                {buildSteps.map((item, index) => (
                  <div key={item} className={`explore-build-line ${index < visibleBuildSteps ? 'done' : ''}`}>
                    <span>{index < visibleBuildSteps ? '✓' : '•'}</span>
                    <strong>{item}</strong>
                  </div>
                ))}
              </div>
              <div className="explore-loading-bar">
                <span style={{ width: `${Math.min((visibleBuildSteps / buildSteps.length) * 100, 100)}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </ExploreLayout>
  );
}
